import { EmailBatchRow, sameEmailBatch } from './extrasEmailBatch';

/**
 * Regroupement d'affichage : UNE ligne par privatisation ET par partenaire.
 *
 * Le suivi affichait une ligne par type de poste. Pour une même privatisation chez un même prestataire,
 * on se retrouvait avec « Vestiaire x4 » et « Hôtesse d'accueil x2 » sur deux lignes, deux fois le nom du
 * partenaire, deux fois la date, et deux boutons qui déclenchaient pourtant le même et unique email.
 * Demande d'Emeni (2026-08-25) : « une seule ligne avec les types et le nombre séparés par une virgule ».
 *
 * Les horaires peuvent différer d'un poste à l'autre (vestiaire 19h, hôtesses 18h) : ils restent donc
 * attachés à leur poste dans la colonne Type, et la colonne Date affiche l'amplitude totale — c'est elle
 * qui intéresse le partenaire. Les résumer à un seul horaire aurait affiché une information fausse.
 */
export interface GroupedRequest<T> {
    key: string;
    /** Toutes les demandes du groupe, dans l'ordre d'affichage. */
    requests: T[];
    /** Ligne représentative : porte l'identifiant utilisé pour les actions groupées côté serveur. */
    lead: T;
}

const heure = (v?: string | null) => (v ? String(v).slice(0, 5) : '');

/** Amplitude couverte par le groupe : du premier début à la dernière fin. */
export const plageHoraire = <T extends { start_time?: string | null; end_time?: string | null }>(rows: T[]) => {
    const debuts = rows.map((r) => heure(r.start_time)).filter(Boolean).sort();
    const fins = rows.map((r) => heure(r.end_time)).filter(Boolean).sort();
    if (!debuts.length) return '';
    const fin = fins.length ? fins[fins.length - 1] : '';
    return fin ? `${debuts[0]} → ${fin}` : debuts[0];
};

/** Vrai si toutes les lignes partagent le même horaire — auquel cas inutile de le répéter par poste. */
export const memeHoraire = <T extends { start_time?: string | null; end_time?: string | null }>(rows: T[]) =>
    new Set(rows.map((r) => `${heure(r.start_time)}-${heure(r.end_time)}`)).size <= 1;

export const groupByPartnerAndEvent = <T extends EmailBatchRow>(rows: T[]): GroupedRequest<T>[] => {
    const groupes = new Map<string, T[]>();
    for (const row of rows) {
        // Une demande sans privatisation ne se regroupe avec rien : elle reste seule sur sa ligne.
        const cle = row.event_id ? `${row.event_id}::${row.partner_id ?? ''}` : `seule::${row.request_id}`;
        if (!groupes.has(cle)) groupes.set(cle, []);
        groupes.get(cle)!.push(row);
    }
    return Array.from(groupes.entries()).map(([key, requests]) => ({ key, requests, lead: requests[0] }));
};

/**
 * Postes du groupe dont le besoin enregistré sur la privatisation dépasse ce qui est réellement pourvu
 * (demandes partenaire + salariés internes bookés).
 *
 * Relever un besoin depuis "Modifier" n'envoie volontairement rien au prestataire — seul "Booker" commande
 * (règle d'Emeni). Mais rien ne signalait l'écart : le 2026-08-25, une privatisation avait besoin de
 * 4 hôtesses et n'en avait commandé que 2, sans qu'aucun écran ne le dise. Ce repère rend l'écart visible
 * là où on regarde le dossier.
 *
 * Dédoublonné par poste : un même poste réparti en plusieurs sous-groupes horaires occupe plusieurs
 * demandes, mais son besoin est un seul chiffre — le répéter afficherait l'alerte deux fois.
 */
export interface BesoinRow {
    extra_type_id?: string | null;
    type_label?: string;
    need_total?: number;
    covered_total?: number;
}

export const postesIncomplets = <T extends BesoinRow>(rows: T[]) => {
    const parType = new Map<string, { typeId: string; label: string; besoin: number; pourvu: number }>();
    rows.forEach((r) => {
        const typeId = String(r.extra_type_id || '');
        if (!typeId || parType.has(typeId)) return;
        parType.set(typeId, {
            typeId,
            label: r.type_label || '',
            besoin: Number(r.need_total) || 0,
            pourvu: Number(r.covered_total) || 0,
        });
    });
    return Array.from(parType.values()).filter((t) => t.besoin > t.pourvu);
};

/** Les demandes du groupe qui partiront réellement dans le prochain email (ni annulées, ni confirmées). */
export const aEnvoyer = <T extends EmailBatchRow>(g: GroupedRequest<T>, toutes: T[]): T[] =>
    sameEmailBatch(toutes, g.lead);
